'use client';

import React, { useEffect, useState } from 'react';

import { ProPaymentPlans } from '../../../components/ProPaymentPlans/ProPaymentPlans';
import { StickyBannerGetPro } from '../../../components/StickyBottomBanner/StickyBannerGetPro';
import { PRIMARY_OFFER_ID, type ProPlanId } from '../../../constants/premium';
import { trackEvent } from '../../../helpers/analytics/trackEvent';
import { useAnalytics } from '../../../contexts/AnalyticsContext/AnalyticsContext';
import { useCurrentURL } from '../../../helpers/hooks/useCurrentURL/useCurrentURL';
import {
  companiesURLParameters,
  flowsURLParameters,
  URLParameters,
} from '../../Onboarding/constants/url';
import { useUserInfo } from '../../../helpers/hooks/useUserInfo';
import { useCoupon } from '../../../helpers/hooks/useCoupon';
import { useIsMobileDevice } from '../../../helpers/hooks/useIsMobileDevice';
import { useDistanceFromTop } from '../../../helpers/hooks/useDistanceFromTop';
import { useScreenHeight } from '../../../helpers/hooks/useScreenHeight';
import { Redirect } from '../../../components/Redirect/Redirect';

export const ProPaymentPlansSection = ({ couponCode }: { couponCode?: string }) => {
  const { isPremium, premiumEndDate } = useUserInfo();
  const { coupon, isLoading: couponIsLoading } = useCoupon(couponCode);
  const [proPlanId, setProPlanId] = useState<ProPlanId | undefined>();
  const [userHasBeenPro, setUserHasBeenPro] = useState(false);

  const isMobileDevice = useIsMobileDevice();
  const distanceFromTop = useDistanceFromTop();
  const screenHeight = useScreenHeight();

  const { currentPage } = useAnalytics();
  const { addParameters, hasParameters } = useCurrentURL();
  const submitLink = addParameters({
    [URLParameters.Company]: companiesURLParameters.fishbrain,
    [URLParameters.Flow]: flowsURLParameters.fishbrain.becomePro,
  });
  const hasOnboardingUrlParams = hasParameters([URLParameters.Company, URLParameters.Flow]);

  const handleSubmitLinkClick = (): void => {
    sessionStorage.setItem('subscription.plan.id', JSON.stringify(proPlanId));
    void trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        type: 'pro',
        source: 'pro-payment-plan',
        page: currentPage,
      },
    });
  };

  useEffect(() => {
    if (premiumEndDate) {
      const currentDate = new Date();
      const result = premiumEndDate.localeCompare(currentDate.toString());
      setUserHasBeenPro(result <= 0);
    }
  }, [premiumEndDate]);

  useEffect(() => {
    const handleSessionStorageChange = (): void => {
      const planIdFromStorage = sessionStorage.getItem('subscription.plan.id');
      const planFromStorage = planIdFromStorage
        ? JSON.parse(planIdFromStorage as ProPlanId)
        : PRIMARY_OFFER_ID;
      setProPlanId(planFromStorage);
    };

    window.addEventListener('planInStorageChanged', handleSessionStorageChange);

    handleSessionStorageChange();

    return () => {
      window.removeEventListener('planInStorageChanged', handleSessionStorageChange);
    };
  }, []);

  useEffect(() => {
    if (coupon) {
      setProPlanId(coupon.group.stripe_plan.external_id);
    }
  }, [coupon]);

  if (isPremium && !hasOnboardingUrlParams) {
    return <Redirect to="/pro/overview" />;
  }

  return (
    <>
      <ProPaymentPlans
        className="col-start-1 row-start-2 px-6"
        coupon={coupon}
        couponIsLoading={couponIsLoading}
        backgroundColour="bg-sea-foam"
        submitLink={submitLink}
        onSubmitLinkClick={handleSubmitLinkClick}
        proPlanId={proPlanId}
        onPlanSelect={setProPlanId}
        userHasBeenPro={userHasBeenPro}
      />{' '}
      {isMobileDevice && distanceFromTop > (screenHeight / 3) * 2 && (
        <StickyBannerGetPro
          selectedPlanId={proPlanId}
          coupon={coupon}
          couponIsLoading={couponIsLoading}
          submitLink={submitLink}
        />
      )}
    </>
  );
};
